/**
 * @module use-cases/utilities/flux/create-async-action
 * @desc Helper functions for creating actions.
 *
 */

import { createActionRtk } from '../lib';
import { createAction } from './create-action';
import { REQUEST_STATE } from './request-state';

import type { ErrorResponse, Meta } from './flux.d';

/**
 * Creates an action that will be dispatched on failure request response.
 *
 * @template T - The action Type.
 * @template M - The action Meta.
 *
 * @example createActionFailure<'userFetchFailure', MetaType>('userFetchFailure');
 */
function createActionFailure<T extends string, M = undefined>(type: T) {
  return createActionRtk(
    type,
    (error: ErrorResponse, meta?: Meta<Partial<{ payload: M; response: any }>>) => ({
      payload: undefined,
      meta,
      error,
    }),
  );
}

/**
 * Creates an action that will be dispatched on success request response.
 *
 * @template T - The action Type.
 * @template P - The action Payload.
 * @template M - The action Meta.
 *
 * @example createActionSuccess<'userFetchSuccess', DataType, MetaType>('userFetchSuccess');
 */
function createActionSuccess<T extends string, P = undefined, M = undefined>(type: T) {
  return createActionRtk(type, (payload: P, meta: Meta<Partial<{ payload: M }>>) => ({
    payload,
    meta,
  }));
}

/**
 * Automatically creates an object with the async states (request, success, failure) and the correct async flow prefixes ([actionName]Request, [actionName]Success, [actionName]Failure).
 *
 * @template T - The action Type prefix - ex. userFetch .
 * @template R - The action request payload type.
 * @template S - The action success pyload type.
 * @template M - The action Meta.
 *
 * @example createAsyncAction<'userFetch', RequestPayloadType, SuccessPayloadType, MetaType>('userFetch');
 */
export function createAsyncAction<T extends string, R = undefined, S = undefined, M = undefined>(
  prefix: T,
) {
  return {
    request: createAction<`${typeof prefix}${REQUEST_STATE.REQUEST}`, R>(
      `${prefix}${REQUEST_STATE.REQUEST}`,
    ),

    success: createActionSuccess<`${typeof prefix}${REQUEST_STATE.SUCCESS}`, S, M>(
      `${prefix}${REQUEST_STATE.SUCCESS}`,
    ),

    failure: createActionFailure<`${typeof prefix}${REQUEST_STATE.FAILURE}`, M>(
      `${prefix}${REQUEST_STATE.FAILURE}`,
    ),
  };
}
